import React, { useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { crm, fmtDate, fmtDateTime, inr } from '../../api/crm';
import ClientProfile from './ClientProfile';
import LeadDetails from './leads/LeadDetails';
import { PlanText, webinarStage } from './leads/LeadsBoard';
import { Initials, Tag } from './leads/ui';

// SupportSearch is the support user's only way to a customer: type one full number, see
// what the sales team sees when they open that lead (app details and webinar details),
// read-only. There is no list to browse; the server answers exact numbers only.

// "+91 98765 43210" -> "9876543210"; '' when it is not a full number.
const fullNumber = (raw) => {
  const s = String(raw || '').trim();
  if (!s || /[^0-9+\- ]/.test(s)) return '';
  let digits = s.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return digits.length === 10 ? digits : '';
};

const nameOf = (lead) => (lead.name && !/^webinar_/.test(lead.name) ? lead.name : '');
const yesNo = (v) => (v ? 'Yes' : 'No');

const Fact = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>{value === '' || value === null || value === undefined ? '—' : value}</Typography>
  </Box>
);

const Heading = ({ title, tag }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
    <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
    {tag}
  </Box>
);

const Note = ({ children }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Paper>
);

const WebinarDetails = ({ lead }) => {
  const stage = webinarStage(lead);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        <Fact label="Stage" value={<Tag tone={stage.tone}>{stage.label}</Tag>} />
        <Fact label="Registered" value={fmtDateTime(lead.webinar_registered_at)} />
        <Fact label="Webinar" value={lead.webinar_name} />
        <Fact label="Attended" value={yesNo(lead.webinar_attended)} />
        <Fact label="Webinar payment" value={lead.webinar_paid_amount > 0 ? `${inr(lead.webinar_paid_amount)} · ${fmtDate(lead.webinar_paid_at)}` : 'None'} />
        <Fact label="Joined the app" value={lead.is_app_user ? `Yes · ${fmtDate(lead.installed_at)}` : 'No'} />
        <Fact label="Bought a plan" value={lead.paid_count > 0 ? `Yes · ${inr(lead.lifetime_paid)}` : 'No'} />
        <Fact label="Dialed by sales" value={yesNo(lead.dialed)} />
      </Box>
      {lead.remarks && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Fact label="Sales remarks" value={lead.remarks} />
        </Box>
      )}
    </Paper>
  );
};

const Result = ({ lead }) => (
  <Stack spacing={1.5}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Initials name={nameOf(lead)} seed={lead.number} size={40} />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.3 }}>{nameOf(lead) || lead.number}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {lead.number}{lead.email ? ` · ${lead.email}` : ''}
        </Typography>
      </Box>
      {(lead.algo_plan || lead.research_plan) && <PlanText lead={lead} />}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {lead.is_app_user && <Tag tone="success">App user</Tag>}
        {lead.is_webinar_user && <Tag tone="info">Webinar</Tag>}
        <Tag tone={lead.owner ? 'neutral' : 'warning'}>{lead.owner ? `Sales: ${lead.owner}` : 'No sales person'}</Tag>
      </Box>
    </Paper>

    <Heading title="App lead details" tag={!lead.is_app_user && <Tag>Not on the app</Tag>} />
    {lead.is_app_user ? (
      <>
        <LeadDetails lead={lead} readOnly />
        <ClientProfile key={lead.number} number={lead.number} embedded />
      </>
    ) : (
      <>
        <Note>
          This number has not installed the app.
          {!lead.is_webinar_user && ' It is known only because the sales team sent it a payment link.'}
        </Note>
        {!lead.is_webinar_user && <LeadDetails lead={lead} readOnly />}
      </>
    )}

    <Heading title="Webinar lead details" tag={!lead.is_webinar_user && <Tag>No webinar</Tag>} />
    {lead.is_webinar_user ? <WebinarDetails lead={lead} /> : <Note>This number has not registered for a webinar.</Note>}
  </Stack>
);

const SupportSearch = () => {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState('');
  // status: idle | loading | found | missing | error
  const [result, setResult] = useState({ status: 'idle' });
  const latest = useRef(0);

  const search = async (e) => {
    if (e) e.preventDefault();
    const number = fullNumber(input);
    if (!number) {
      setInputError('Enter the full 10-digit mobile number');
      return;
    }
    setInputError('');
    setResult({ status: 'loading', number });
    const id = ++latest.current;
    try {
      const data = await crm('leads/lookup', { query: { number } });
      if (id !== latest.current) return; // a newer search is running
      const lead = (data.leads || [])[0];
      setResult(lead ? { status: 'found', number, lead } : { status: 'missing', number });
    } catch (err) {
      if (id !== latest.current) return;
      setResult({ status: 'error', number, message: err.message });
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 1200 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Look up a customer</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Enter the customer&apos;s full mobile number to see their app details and webinar details.
        </Typography>
        <Box component="form" onSubmit={search} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            autoFocus
            value={input}
            onChange={(e) => { setInput(e.target.value.replace(/[^0-9+\- ]/g, '').slice(0, 16)); setInputError(''); }}
            placeholder="10-digit mobile number"
            error={Boolean(inputError)}
            helperText={inputError}
            inputProps={{ inputMode: 'tel', 'aria-label': 'Customer mobile number' }}
            sx={{ flex: '1 1 220px', maxWidth: 320 }}
          />
          <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={result.status === 'loading'} sx={{ height: 37 }}>
            Search
          </Button>
        </Box>
      </Paper>

      {result.status === 'loading' && <Box sx={{ p: 1 }}><CircularProgress size={22} /></Box>}
      {result.status === 'error' && (
        <Alert severity="error">Could not look up {result.number}: {result.message}. Try again.</Alert>
      )}
      {result.status === 'missing' && (
        <Note>
          Nothing found for <b>{result.number}</b>. This number has not installed the app, registered for a webinar or received a
          payment link. Check the number and search again.
        </Note>
      )}
      {result.status === 'found' && <Result key={result.number} lead={result.lead} />}
    </Stack>
  );
};

export default SupportSearch;
